import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FavoritesEmptyHero from "@/components/FavoritesEmptyHero";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useLibraryContent } from "@/hooks/useLibraryContent";
import { useGeneratedContent } from "@/hooks/useGeneratedContent";
import { useTrashManagement } from "@/hooks/useTrashManagement";
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { FavoritesSkeleton } from "@/components/skeletons/FavoritesSkeleton";
import { CorkBoardView } from "@/components/CorkBoardView";
import { CorkModernView } from "@/components/CorkModernView";
import { ContentCard } from "@/components/ContentCard";
import { ContentDetailModal } from "@/components/ContentDetailModal";
import { ContentIdea } from "@/types/dashboard";
import { useLanguage } from "@/contexts/LanguageContext";
import { TabButton } from "@/components/ui/tab-button";
import {
  Filter,
  Star,
  Heart,
  RefreshCw,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Lightbulb,
  LayoutGrid,
  List,
  TrendingUp
} from "lucide-react";

const DEFAULT_FILTERS = {
  sourceAccount: 'all',
  viralScore: 'all',
  dateSort: 'newest',
  sortBy: 'date',
  timeRange: 'all'
};

const Favorites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'inspiration' | 'generated'>('all');
  const [localFilters, setLocalFilters] = useState(DEFAULT_FILTERS);
  const [selectedIdea, setSelectedIdea] = useState<ContentIdea | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'cork_classic' | 'cork_modern' | 'list'>(() => {
    const saved = localStorage.getItem('favorites-view-mode');
    if (saved === 'cork_classic' || saved === 'cork_modern' || saved === 'list') return saved;
    // Migrate old 'cork' value
    if (saved === 'cork') return 'cork_classic';
    return 'cork_modern';
  });
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const {
    isLoading: isLoadingLibrary,
    contentIdeas,
    error: errorLibrary,
    copyToClipboard,
    refresh: refreshLibrary
  } = useLibraryContent(user?.id || null);

  const {
    isLoading: isLoadingGenerated,
    generatedIdeas,
    error: errorGenerated,
    refresh: refreshGenerated
  } = useGeneratedContent(user?.id || null);

  const { moveToTrash } = useTrashManagement();

  const isLoading = isLoadingLibrary || isLoadingGenerated;
  const error = errorLibrary || errorGenerated;

  const refresh = () => {
    refreshLibrary();
    refreshGenerated();
  };

  const handleTrashAction = async (ideaId: string, source: 'scraped' | 'user_generated') => {
    const success = await moveToTrash(ideaId, source);
    if (success) {
      refresh();
    }
  };

  // Combine all content
  const allContent = [...contentIdeas, ...generatedIdeas];

  // Get only favorited content
  const favoriteIdeas = allContent.filter(idea => idea.is_favorited);

  // Filter by content type based on active tab
  const tabFilteredFavorites = activeTab === 'all'
    ? favoriteIdeas
    : activeTab === 'inspiration'
    ? favoriteIdeas.filter(idea => idea.source === 'scraped')
    : favoriteIdeas.filter(idea => idea.source === 'user_generated');

  // Apply local filters to tab-filtered favorite content
  const filteredFavorites = tabFilteredFavorites.filter(idea => {
    // Source account filter
    if (localFilters.sourceAccount !== 'all') {
      const sourceMatch = idea.source_competitor?.username === localFilters.sourceAccount;
      if (!sourceMatch) return false;
    }

    // Viral score filter (matching Library thresholds)
    if (localFilters.viralScore !== 'all') {
      const score = idea.viral_score || 0;
      switch (localFilters.viralScore) {
        case 'high':
          if (score < 70) return false;
          break;
        case 'medium':
          if (score < 40 || score >= 70) return false;
          break;
        case 'low':
          if (score >= 40) return false;
          break;
      }
    }

    // Time range filter
    if (localFilters.timeRange !== 'all') {
      const ideaDate = new Date(idea.favorited_at || idea.created_at);
      const now = new Date();
      const daysAgo = (now.getTime() - ideaDate.getTime()) / (1000 * 60 * 60 * 24);

      switch (localFilters.timeRange) {
        case 'week':
          if (daysAgo > 7) return false;
          break;
        case '2weeks':
          if (daysAgo > 14) return false;
          break;
        case 'month':
          if (daysAgo > 30) return false;
          break;
        case '3months':
          if (daysAgo > 90) return false;
          break;
      }
    }

    return true;
  }).sort((a, b) => {
    // Sorting
    if (localFilters.sortBy === 'viral') {
      return (b.viral_score || 0) - (a.viral_score || 0);
    }
    // Sort by date
    if (localFilters.dateSort === 'newest') {
      return new Date(b.favorited_at || b.created_at).getTime() - new Date(a.favorited_at || a.created_at).getTime();
    }
    return new Date(a.favorited_at || a.created_at).getTime() - new Date(b.favorited_at || b.created_at).getTime();
  });

  // Pagination calculations (for list view)
  const totalPages = Math.ceil(filteredFavorites.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFavorites = filteredFavorites.slice(startIndex, endIndex);

  // Reset to page 1 when filters or tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [localFilters, itemsPerPage, activeTab]);

  // Scroll to top when page changes
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Update selectedIdea when underlying data changes (after refresh)
  useEffect(() => {
    if (selectedIdea && isModalOpen) {
      const updatedIdea = allContent.find(idea => idea.id === selectedIdea.id);
      if (updatedIdea) {
        setSelectedIdea(updatedIdea);
      }
    }
  }, [contentIdeas, generatedIdeas]);

  // Get unique source accounts for filter dropdown (from tab-filtered favorites)
  const sourceAccounts = Array.from(
    new Set(
      tabFilteredFavorites
        .map(idea => idea.source_competitor?.username)
        .filter(Boolean)
    )
  );

  const handleIdeaClick = (idea: ContentIdea) => {
    setSelectedIdea(idea);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedIdea(null);
  };

  const setViewModeAndSave = (mode: 'cork_classic' | 'cork_modern' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('favorites-view-mode', mode);
  };

  // Loading State
  if (isLoading) {
    return <FavoritesSkeleton />;
  }

  // Error State
  if (error) {
    return (
      <main className="flex-1 p-3 md:p-6 overflow-y-auto">
        <div className="text-center space-y-6">
          <div className="w-16 h-16 mx-auto bg-destructive rounded-full flex items-center justify-center">
            <RefreshCw className="h-8 w-8 text-destructive-foreground" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">{t('favorites.failedToLoad')}</h1>
            <p className="text-xl text-muted-foreground mb-6">{error}</p>
            <Button onClick={refresh} size="lg">
              <RefreshCw className="mr-2 h-5 w-5" />
              {t('common.tryAgain')}
            </Button>
          </div>
        </div>
      </main>
    );
  }

  // Empty State
  if (favoriteIdeas.length === 0) {
    return (
      <main className="flex-1 p-3 md:p-6 overflow-y-auto flex justify-center">
        <div className="w-full max-w-[1200px]">
          <FavoritesEmptyHero />
        </div>
      </main>
    );
  }

  return (
    <>
      <main ref={scrollContainerRef} className="flex-1 p-3 md:p-6 pb-16 flex justify-center overflow-y-auto">
        <div className="w-full max-w-[1200px] space-y-4 md:space-y-8 pb-16">

            {/* Content Type Tabs */}
            <div className="glass-card rounded-xl p-1 border border-border/50 inline-flex flex-wrap gap-1">
              <TabButton
                variant="gradient"
                isActive={activeTab === 'all'}
                onClick={() => setActiveTab('all')}
                className="flex items-center gap-2"
              >
                <Heart className="h-4 w-4" />
                {t('favorites.tabs.all')}
                <Badge variant="outline" className="ml-1 bg-background/50 border-border/50 text-foreground font-semibold">
                  {favoriteIdeas.length}
                </Badge>
              </TabButton>

              <TabButton
                variant="gradient"
                isActive={activeTab === 'inspiration'}
                onClick={() => setActiveTab('inspiration')}
                className="flex items-center gap-2"
              >
                <Lightbulb className="h-4 w-4" />
                {t('favorites.tabs.inspiration')}
                <Badge variant="outline" className="ml-1 bg-background/50 border-border/50 text-foreground font-semibold">
                  {favoriteIdeas.filter(idea => idea.source === 'scraped').length}
                </Badge>
              </TabButton>

              <TabButton
                variant="gradient"
                isActive={activeTab === 'generated'}
                onClick={() => setActiveTab('generated')}
                className="flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4" />
                {t('favorites.tabs.generated')}
                <Badge variant="outline" className="ml-1 bg-background/50 border-border/50 text-foreground font-semibold">
                  {favoriteIdeas.filter(idea => idea.source === 'user_generated').length}
                </Badge>
              </TabButton>
            </div>

            {/* View Toggle - matching Library styling */}
            <div className="glass-card p-3 rounded-xl border border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-foreground text-sm">{t('library.view')}</span>
                </div>

                <div className="flex items-center">
                  <button
                    onClick={() => setViewModeAndSave('cork_classic')}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all rounded-l-lg border ${
                      viewMode === 'cork_classic'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span>📌</span>
                    <span className="hidden sm:inline">Cork Classic</span>
                  </button>
                  <button
                    onClick={() => setViewModeAndSave('cork_modern')}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all border-t border-b ${
                      viewMode === 'cork_modern'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span>✨</span>
                    <span className="hidden sm:inline">Cork Modern</span>
                  </button>
                  <button
                    onClick={() => setViewModeAndSave('list')}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all rounded-r-lg border ${
                      viewMode === 'list'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <List className="h-4 w-4" />
                    <span className="hidden sm:inline">Lista</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Filters - matching Library layout */}
            <div className="glass-card p-3 rounded-xl border border-border/50">
              <div className="flex flex-col gap-3">
                {/* Top Row: Header and Actions */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 h-8 text-sm"
                  >
                    <Filter className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-foreground">{t('favorites.filters')}</span>
                    <ChevronRight className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-90' : ''}`} />
                  </Button>

                  <div className="flex items-center gap-2">
                    {showFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setLocalFilters(DEFAULT_FILTERS)}
                        className="h-8 text-xs"
                      >
                        {t('common.clear')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Filters Row - matching Library 5-column grid */}
                {showFilters && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {/* Viral Score Filter - Hide on Generated tab */}
                    {activeTab !== 'generated' && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs text-muted-foreground">{t('favorites.score')}</label>
                        <Select
                          value={localFilters.viralScore}
                          onValueChange={(value) => setLocalFilters(prev => ({ ...prev, viralScore: value }))}
                        >
                          <SelectTrigger className="h-9 w-full text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t('favorites.allScores')}</SelectItem>
                            <SelectItem value="high">Wysoki (70+)</SelectItem>
                            <SelectItem value="medium">Średni (40-69)</SelectItem>
                            <SelectItem value="low">Niski (0-39)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Time Range Filter */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground">Okres</label>
                      <Select
                        value={localFilters.timeRange}
                        onValueChange={(value) => setLocalFilters(prev => ({ ...prev, timeRange: value }))}
                      >
                        <SelectTrigger className="h-9 w-full text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Wszystkie</SelectItem>
                          <SelectItem value="week">Ostatni tydzień</SelectItem>
                          <SelectItem value="2weeks">2 tygodnie</SelectItem>
                          <SelectItem value="month">Miesiąc</SelectItem>
                          <SelectItem value="3months">3 miesiące</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Source Account Filter */}
                    {sourceAccounts.length > 0 && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs text-muted-foreground">{t('favorites.source')}</label>
                        <Select
                          value={localFilters.sourceAccount}
                          onValueChange={(value) => setLocalFilters(prev => ({ ...prev, sourceAccount: value }))}
                        >
                          <SelectTrigger className="h-9 w-full text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t('favorites.allSources')}</SelectItem>
                            {sourceAccounts.map(account => (
                              <SelectItem key={account} value={account || ''}>
                                @{account}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Sort By */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-muted-foreground">Sortuj</label>
                      <Select
                        value={localFilters.sortBy}
                        onValueChange={(value) => setLocalFilters(prev => ({ ...prev, sortBy: value }))}
                      >
                        <SelectTrigger className="h-9 w-full text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Po dacie</SelectItem>
                          <SelectItem value="viral">Najlepsze</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Date Sort - Only show when sorting by date */}
                    {localFilters.sortBy === 'date' && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs text-muted-foreground">{t('favorites.date')}</label>
                        <Select
                          value={localFilters.dateSort}
                          onValueChange={(value) => setLocalFilters(prev => ({ ...prev, dateSort: value }))}
                        >
                          <SelectTrigger className="h-9 w-full text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="newest">{t('favorites.newestFirst')}</SelectItem>
                            <SelectItem value="oldest">{t('favorites.oldestFirst')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Content Views */}
            {viewMode === 'cork_classic' ? (
              <CorkBoardView
                ideas={filteredFavorites}
                onIdeaClick={handleIdeaClick}
              />
            ) : viewMode === 'cork_modern' ? (
              <CorkModernView
                ideas={filteredFavorites}
                onIdeaClick={handleIdeaClick}
                onFavoriteToggle={refresh}
                onUsedToggle={refresh}
              />
            ) : (
              <>
                {/* Content Count and Items Per Page */}
                <div className="flex items-center justify-between px-1">
                  <p className="text-sm text-muted-foreground">
                    {language === 'pl'
                      ? `${startIndex + 1}-${Math.min(endIndex, filteredFavorites.length)} z ${filteredFavorites.length}`
                      : `${startIndex + 1}-${Math.min(endIndex, filteredFavorites.length)} of ${filteredFavorites.length}`
                    }
                  </p>

                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => setItemsPerPage(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-20 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="30">30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-card rounded-2xl p-4 md:p-6 shadow-sm border border-border/30">
                  <div className="flex flex-col gap-4" id="content-grid">
                    {paginatedFavorites.map((idea) => (
                      <ContentCard
                        key={idea.id}
                        idea={idea}
                        onCopyScript={(content) => copyToClipboard(content, 'Script')}
                        onFavoriteToggle={refresh}
                        isRowLayout={true}
                        isExpanded={expandedCardId === idea.id}
                        onToggleExpand={() => setExpandedCardId(expandedCardId === idea.id ? null : idea.id)}
                        onTrashAction={handleTrashAction}
                      />
                    ))}
                  </div>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(page)}
                              className="w-10"
                            >
                              {page}
                            </Button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} className="px-2">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Empty Filtered Results */}
            {filteredFavorites.length === 0 && tabFilteredFavorites.length > 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                  <Filter className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t('favorites.noMatch')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t('favorites.noMatchDesc')}
                </p>
                <Button
                  variant="outline"
                  onClick={() => setLocalFilters(DEFAULT_FILTERS)}
                >
                  {t('favorites.clearAllFilters')}
                </Button>
              </div>
            )}

            {/* Stats Footer - matching Library style */}
            <div className="glass-card p-8 rounded-2xl border border-white/10 shadow-elevated">
              <div className="flex items-center justify-center gap-12 text-sm flex-wrap">
                <div className="flex items-center gap-3 group hover:scale-110 transition-transform duration-300">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-red-500/20 to-pink-500/20">
                    <Heart className="h-5 w-5 text-red-500 fill-red-500" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold text-foreground">{favoriteIdeas.length}</div>
                    <div className="text-xs text-muted-foreground">{t('favorites.favoriteIdeas')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 group hover:scale-110 transition-transform duration-300">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold text-foreground">
                      {favoriteIdeas.filter(i => i.source === 'scraped').length}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('favorites.tabs.inspiration')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 group hover:scale-110 transition-transform duration-300">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20">
                    <Star className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold text-foreground">
                      {(() => {
                        const scored = favoriteIdeas.filter(i => i.source !== 'user_generated' && typeof i.viral_score === 'number');
                        if (scored.length === 0) return 0;
                        const avg = scored.reduce((sum, i) => sum + (i.viral_score as number), 0) / scored.length;
                        return Math.round(avg);
                      })()}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('favorites.avgScore')}</div>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ContentDetailModal
        idea={selectedIdea}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onCopyScript={(content) => copyToClipboard(content, 'Script')}
        onFavoriteToggle={() => {
          refresh();
        }}
        onUsedToggle={() => {
          refresh();
        }}
        onTrash={handleTrashAction}
      />
    </>
  );
};

export default Favorites;
